/**
 * Where Google puts a delivery address, for the Shipments page's "Find on map" dialog
 * (CoordinateConfirmDialog).  The kitchen asks Google itself, through the Maps JavaScript
 * API geocoder - a browser key restricted to the kitchen's sites cannot call the Geocoding
 * web service - and the api only ever saves coordinates someone confirmed.
 *
 * The key is VUE_APP_GOOGLE_API_KEY, the same variable fed-web-3 uses.
 */
import {normalizePostalCode} from '@/utils/address-coordinates';

const PRECISE_LOCATION_TYPES = ['ROOFTOP', 'RANGE_INTERPOLATED', 'GEOMETRIC_CENTER'];

let mapsLoading = null;

/** the google.maps namespace, loading the Maps JavaScript API the first time */
export function loadGoogleMaps() {
  if (window.google && window.google.maps && window.google.maps.Geocoder) {
    return Promise.resolve(window.google.maps);
  }
  if (!mapsLoading) {
    const key = process.env.VUE_APP_GOOGLE_API_KEY;
    if (!key) {
      return Promise.reject(new Error('Google Maps is not set up for the kitchen (no VUE_APP_GOOGLE_API_KEY)'));
    }
    mapsLoading = new Promise((resolve, reject) => {
      const callback = `fedKitchenMapsLoaded${Date.now()}`;
      window[callback] = () => {
        delete window[callback];
        resolve(window.google.maps);
      };
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&v=weekly&callback=${callback}`;
      script.async = true;
      script.onerror = () => {
        mapsLoading = null;
        reject(new Error('Google Maps did not load'));
      };
      document.head.appendChild(script);
    });
  }
  return mapsLoading;
}

/** a delivery address as one line, for google */
export function addressText(address) {
  if (!address) return '';
  if (typeof address === 'string') return address;
  return [address.line1, address.city, address.state, address.postal_code, address.country || 'Canada']
    .filter(Boolean).join(', ');
}

/** the geocoder request: the place id the signup form stored when there is one, else the text */
export function geocodeRequest(address) {
  if (address && typeof address === 'object' && address.place_id) {
    return {placeId: address.place_id};
  }
  return {address: addressText(address), componentRestrictions: {country: 'CA'}};
}

/**
 * the first result as coordinates for the address, with `warnings` for a pin a driver
 * could not use as it is: an approximate match (a city centre), or a partial match on
 * another postal code (most likely the wrong house).  someone checks the pin on the map
 * before it is saved, so these are shown rather than refused.
 */
export function describeResult(result, address) {
  const location = result.geometry.location;
  const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
  const lng = typeof location.lng === 'function' ? location.lng() : location.lng;
  const locationType = result.geometry.location_type;
  const warnings = [];
  if (!PRECISE_LOCATION_TYPES.includes(locationType)) {
    warnings.push(`Google only knows the area (${locationType || 'precision unknown'}), not the house`);
  }
  if (result.partial_match && address && typeof address === 'object') {
    const postal = (result.address_components || []).find(c => (c.types || []).includes('postal_code'));
    if (normalizePostalCode(postal && postal.long_name) !== normalizePostalCode(address.postal_code)) {
      warnings.push(`Google only partly matched the address, in another postal code`);
    }
  }
  return {
    lat,
    lng,
    source: address && typeof address === 'object' && address.place_id ? 'place_id' : 'address',
    locationType,
    placeId: result.place_id,
    formattedAddress: result.formatted_address,
    warnings,
  };
}

/** where google puts the address; `maps` (google.maps) is injectable for tests */
export async function geocodeAddress(address, {maps} = {}) {
  const googleMaps = maps || await loadGoogleMaps();
  const geocoder = new googleMaps.Geocoder();
  let results;
  try {
    ({results} = await geocoder.geocode(geocodeRequest(address)));
  } catch (e) {
    // the geocoder rejects with a code such as ZERO_RESULTS or REQUEST_DENIED
    throw new Error(`Google could not place this address (${e.code || e.message})`);
  }
  if (!results || !results.length) {
    throw new Error('Google could not place this address (ZERO_RESULTS)');
  }
  return describeResult(results[0], address);
}
